import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable, tap } from 'rxjs';

import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { LANGUAGE_SETTING, LanguagePreferenceService } from '../../services/language-preference.service';
import { languageOptions, SettingsPageStore } from './settings-page.store';

// Backend #670: Customer-scoped boolean ("true" / "false"). When on, the buyer's order
// confirmation is also sent to the customer's contact e-mail on top of the user who placed
// the order. Off by default; the backend snapshots it at checkout.
export const NOTIFY_CUSTOMER_EMAIL_ON_ORDER_SETTING = 'NotifyCustomerEmailOnOrder';

function buildNotificationForm(fb: FormBuilder) {
  return fb.group({
    notifyCustomerEmailOnOrder: [false],
    customerLanguage: [''],
  });
}

/**
 * #670: the customer-wide transaction-mail switches (Benachrichtigungen tab), and since #178
 * slice B the language the customer account *sends* in. Customer-scoped, so the page shows it
 * to admins of any customer — unlike the printer, not only to retailers.
 */
@Component({
  selector: 'app-notification-settings',
  imports: [ReactiveFormsModule, AsyncClickDirective, RouterModule],
  templateUrl: './notification-settings.component.html',
})
export class NotificationSettingsComponent {
  private readonly store = inject(SettingsPageStore);
  private readonly languagePreference = inject(LanguagePreferenceService);

  readonly profile = this.store.profile;
  readonly form = buildNotificationForm(inject(FormBuilder));
  /** See {@link patch} for the extra entry while the customer has chosen no language. */
  customerLanguages = languageOptions();

  constructor() {
    this.store.patchOnRead(EntityTypeConstants.EntityTypeCustomer, this.form, () => this.patch());
  }

  private patch(): void {
    const scope = EntityTypeConstants.EntityTypeCustomer;
    // #670: the stored value is the string "true" / "false"; anything else reads as off, like the
    // backend's own SettingKeys.ReadBoolean.
    const notify = this.store.value(scope, NOTIFY_CUSTOMER_EMAIL_ON_ORDER_SETTING);

    // #178 slice B: a customer that has never set a language has no row, and the backend treats
    // that as "no setting was found" rather than as German — on an invoice the distinction is the
    // difference between the house default and the issuer having chosen. So an unset customer
    // gets an extra, unselectable entry saying so, and it disappears once a language is picked:
    // nothing here ever writes a row the admin did not ask for, which is why a save of the
    // checkbox alone cannot silently claim a language.
    const language = this.store.value(scope, LANGUAGE_SETTING);
    const isSet = this.languagePreference.isSupported(language);
    this.customerLanguages = isSet
      ? languageOptions()
      : [{ code: '', name: $localize`:@@settings.language.unset:nicht gesetzt (Standard: Deutsch)` }, ...languageOptions()];

    this.form.patchValue({
      notifyCustomerEmailOnOrder: notify?.toLowerCase() === 'true',
      customerLanguage: isSet ? language : '',
    });
  }

  save(): Observable<unknown> {
    const scope = EntityTypeConstants.EntityTypeCustomer;
    const { notifyCustomerEmailOnOrder, customerLanguage } = this.form.getRawValue();
    const batch = [this.store.row(scope, NOTIFY_CUSTOMER_EMAIL_ON_ORDER_SETTING, notifyCustomerEmailOnOrder ? 'true' : 'false')];

    // #178 slice B: only ever written when an admin actually picked a language. The "nicht
    // gesetzt" entry is a description of the current state, not a value — writing it back would
    // turn "never chosen" into "chose German", which is exactly the claim the invoice must not
    // make on the issuer's behalf.
    if (this.languagePreference.isSupported(customerLanguage)) {
      batch.push(this.store.row(scope, LANGUAGE_SETTING, customerLanguage));
    }

    return this.store.save$(batch).pipe(
      tap(() => {
        this.form.markAsPristine();
        this.store.reload(scope);
      }),
    );
  }
}
